import {
  Component,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import {
  CONFLICT_STATUSES,
  ConflictStatus,
  Post,
  PostPriority
} from '@eview/core/domain/post/post';
import {
  PostUpdate,
  PostUpdateEvidence,
  PostUpdatePayload
} from '@eview/core/domain/post/post-update';
import { PostService } from '@eview/core/domain/post/post.service';
import { PostHelpers } from '@eview/core/domain/post/post.helpers';
import { MediaService } from '@eview/core/domain/media/media.service';
import { getFileNameFromMediaUrl } from '@eview/core/domain/media/media-url.helpers';
import { assertReadableUploadFile } from '@eview/core/domain/media/readable-file.validation';
import { SearchableSelectOption } from '@eview/features/ui/searchable-select/searchable-select.component';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Store } from '@ngrx/store';
import { forkJoin, of, Subscription, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as moment from 'moment';
import { AppState } from '@eview/core/store/states/app.state';
import { selectUser } from '@eview/core/store/selectors/user.selector';
import { User } from '@eview/core/models/user';
import {
  blockIncidentLongTextKeypress,
  blockIncidentLongTextPaste,
  hasInvalidIncidentLongText
} from '../../validators/incident-long-text.validation';
import {
  getEvidenceUrlError,
  isValidEvidenceUrl,
  normalizeEvidenceUrl
} from '../../validators/evidence-url.validation';

interface EvidenceDraft {
  media_id?: number | null;
  source_url?: string;
  caption?: string;
  reviewed_at?: string;
  uploading?: boolean;
  file_url?: string;
  /** Local file kept until the update is saved (not uploaded yet). */
  pendingFile?: File | null;
  /** i18n key for file field validation (size/type/corrupt). */
  fileErrorKey?: string;
}

interface MediaPreview {
  url: string;
  caption?: string;
  mime?: string;
}

/** Allowed evidence uploads (aligned with report file attributes). */
const EVIDENCE_ACCEPT =
  '.pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/jpg,image/png';
const EVIDENCE_ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png'];
const EVIDENCE_ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/pjpeg'
];
const MAX_EVIDENCES_PER_UPDATE = 5;

@Component({
  selector: 'eview-post-updates',
  templateUrl: 'post-updates.component.html',
  styleUrls: ['post-updates.component.scss']
})
export class PostUpdatesComponent implements OnChanges, OnDestroy {
  @Input() post: Post;
  @Input() canEdit = false;
  /** Form grant: publish any update on this form. */
  @Input() canPublishAny = false;
  /** Form grant: publish own updates. */
  @Input() canPublishOwn = false;
  /** @deprecated use canPublishAny / canPublishOwn */
  @Input() set canPublish(v: boolean) {
    if (v && !this.canPublishAny && !this.canPublishOwn) {
      this.canPublishAny = true;
      this.canPublishOwn = true;
    }
  }
  @Input() viewOnlyMode = false;
  /** Anonymous users should not see an empty updates block. */
  @Input() isAnonymous = false;

  private currentUser: User | null = null;

  evidenceUrlTouched = false;

  PostHelpers = PostHelpers;
  evidenceAccept = EVIDENCE_ACCEPT;
  maxEvidences = MAX_EVIDENCES_PER_UPDATE;
  updates: PostUpdate[] = [];
  /** Resolved media URLs keyed by media_id for display/download. */
  mediaPreviews: { [mediaId: number]: MediaPreview } = {};
  loading = false;
  saving = false;
  editingId: number | null = null;
  errorMessage = '';
  formValidateAttempted = false;
  descriptionTouched = false;
  occurredAtTouched = false;
  conflictStatusTouched = false;
  priorityTouched = false;

  draft: {
    description: string;
    occurred_at: string;
    conflict_status: string;
    priority: string;
    published: boolean;
    unpublish_reason: string;
    evidences: EvidenceDraft[];
  } = this.emptyDraft();

  /** Published state when the edit form was opened (to detect unpublish). */
  private wasPublishedOnEdit = false;
  unpublishReason = '';
  unpublishReasonErrorKey: string | null = null;
  readonly unpublishReasonMaxLength = 2000;
  private formModalRef: NgbModalRef | null = null;

  @ViewChild('updateFormModal', { static: false }) updateFormModal: TemplateRef<any>;
  @ViewChild('unpublishModal', { static: false }) unpublishModal: TemplateRef<any>;

  conflictStatusSelectOptions: SearchableSelectOption[] = CONFLICT_STATUSES.map(
    status => ({
      value: status,
      label: PostHelpers.GetConflictStatusLabel(status)
    })
  );

  prioritySelectOptions: SearchableSelectOption[] = [
    { value: PostPriority.Low, label: 'PRIORITY_LOW' },
    { value: PostPriority.Medium, label: 'PRIORITY_MEDIUM' },
    { value: PostPriority.High, label: 'PRIORITY_HIGH' },
    { value: PostPriority.Critical, label: 'PRIORITY_CRITICAL' }
  ];

  private subs = new Subscription();

  constructor(
    private postService: PostService,
    private mediaService: MediaService,
    private modalService: NgbModal,
    private store: Store<AppState>
  ) {
    this.subs.add(
      this.store.select(selectUser).subscribe(u => {
        this.currentUser = u || null;
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.post && this.rootPostId) {
      this.load();
    }
  }

  ngOnDestroy() {
    if (this.formModalRef) {
      this.formModalRef.dismiss('destroy');
      this.formModalRef = null;
    }
    this.revokeAllDraftLocalUrls();
    this.subs.unsubscribe();
  }

  get rootPostId(): number {
    if (!this.post) {
      return null;
    }
    return this.post.parent && this.post.parent.id > 0
      ? this.post.parent.id
      : this.post.id;
  }

  /**
   * Root published + form publish grant: any, or own for the draft/update author.
   */
  get canPublishUpdate(): boolean {
    if (!this.post?.published) {
      return false;
    }
    if (this.canPublishAny) {
      return true;
    }
    if (!this.canPublishOwn || !this.currentUser) {
      return false;
    }
    // New update: current user will be author
    if (this.editingId == null) {
      return true;
    }
    const update = this.updates.find(u => Number(u.id) === Number(this.editingId));
    return this.isUpdateOwnedByCurrentUser(update);
  }

  private isUpdateOwnedByCurrentUser(update?: PostUpdate | null): boolean {
    if (!update || !this.currentUser) {
      return false;
    }
    const ownerId = update.user?.id ?? update.user_id;
    return ownerId != null && Number(ownerId) === Number(this.currentUser.id);
  }

  get canManage(): boolean {
    return !!this.canEdit && !this.viewOnlyMode;
  }

  /** Hide empty updates section for visitors who are not logged in. */
  get shouldShowUpdatesSection(): boolean {
    if (!this.isAnonymous) {
      return true;
    }
    return this.updates.length > 0;
  }

  @HostBinding('class.post-updates-host--hidden')
  get hostHiddenClass(): boolean {
    return !this.shouldShowUpdatesSection;
  }

  get isFormModalOpen(): boolean {
    return !!this.formModalRef;
  }

  /** Upper bound for datetime-local (now), so future dates cannot be picked. */
  get maxOccurredAt(): string {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
      d.getHours()
    )}:${p(d.getMinutes())}`;
  }

  /** Lower bound aligned with form date fields / summary filters. */
  get minOccurredAt(): string {
    return '2018-01-01T00:00';
  }

  /** Upper bound for evidence "reviewed at" date (today). */
  get maxReviewedAt(): string {
    return moment().format('YYYY-MM-DD');
  }

  get minReviewedAt(): string {
    return '2018-01-01';
  }

  load() {
    if (!this.rootPostId) {
      return;
    }
    this.loading = true;
    this.subs.add(
      this.postService.listUpdates(this.rootPostId).subscribe({
        next: res => {
          const results = res?.results || [];
          // Public/viewers only see published updates; editors see all.
          const visible = this.canManage
            ? results
            : results.filter(u => !!u.published);
          this.updates = this.sortUpdatesByOccurredAtDesc(visible);
          this.loading = false;
          this.resolveEvidenceMedia(this.updates);
        },
        error: () => {
          this.updates = [];
          this.loading = false;
        }
      })
    );
  }

  /** Newest fecha/hora first. */
  private sortUpdatesByOccurredAtDesc(updates: PostUpdate[]): PostUpdate[] {
    return [...(updates || [])].sort((a, b) => {
      const aTime = a?.occurred_at ? moment(a.occurred_at).valueOf() : 0;
      const bTime = b?.occurred_at ? moment(b.occurred_at).valueOf() : 0;
      if (bTime !== aTime) {
        return bTime - aTime;
      }
      return (Number(b?.id) || 0) - (Number(a?.id) || 0);
    });
  }

  onAddClick() {
    if (this.isFormModalOpen) {
      return;
    }
    this.editingId = null;
    this.wasPublishedOnEdit = false;
    this.draft = this.emptyDraft();
    this.errorMessage = '';
    this.resetFormFieldValidation();
    this.openFormModal();
  }

  onEditClick(update: PostUpdate) {
    if (this.isFormModalOpen) {
      return;
    }
    this.editingId = update.id;
    this.wasPublishedOnEdit = !!update.published;
    this.draft = {
      description: update.description || '',
      occurred_at: this.toDatetimeLocal(update.occurred_at),
      conflict_status: String(update.conflict_status || ConflictStatus.Activo),
      priority: PostHelpers.NormalizePostPriority(update.priority as string),
      published: !!update.published,
      unpublish_reason: update.unpublish_reason || '',
      evidences: (update.evidences || []).map(e => ({
        media_id: e.media_id || null,
        source_url: e.source_url || '',
        caption: e.caption || '',
        reviewed_at: this.toDateInput(e.reviewed_at),
        file_url: e.media_id
          ? this.mediaPreviews[Number(e.media_id)]?.url || ''
          : ''
      }))
    };
    this.errorMessage = '';
    this.resetFormFieldValidation();
    this.resolveEvidenceMedia([update]);
    this.openFormModal();
  }

  onCancelForm() {
    if (this.saving || this.hasUploadingEvidence()) {
      return;
    }
    if (this.formModalRef) {
      this.formModalRef.dismiss('cancel');
      return;
    }
    this.resetFormState();
  }

  private openFormModal() {
    if (!this.updateFormModal || this.formModalRef) {
      return;
    }
    this.formModalRef = this.modalService.open(this.updateFormModal, {
      ariaLabelledBy: 'post-update-form-title',
      centered: true,
      backdrop: 'static',
      keyboard: false,
      scrollable: true,
      windowClass: 'post-update-form-modal'
    });
    this.formModalRef.result.then(
      () => this.resetFormState(),
      () => this.resetFormState()
    );
  }

  private resetFormState() {
    this.revokeAllDraftLocalUrls();
    this.formModalRef = null;
    this.editingId = null;
    this.wasPublishedOnEdit = false;
    this.errorMessage = '';
    this.resetFormFieldValidation();
    this.draft = this.emptyDraft();
  }

  private resetFormFieldValidation() {
    this.formValidateAttempted = false;
    this.descriptionTouched = false;
    this.occurredAtTouched = false;
    this.conflictStatusTouched = false;
    this.priorityTouched = false;
    this.evidenceUrlTouched = false;
  }

  getSelectInputClass(invalid: boolean): string {
    const base = 'form-control searchable-select__input';
    return invalid ? `${base} is-invalid` : base;
  }

  showDescriptionRequiredError(): boolean {
    return (
      (this.formValidateAttempted || this.descriptionTouched) &&
      !(this.draft.description || '').trim()
    );
  }

  showDescriptionCharError(): boolean {
    return (
      (this.formValidateAttempted || this.descriptionTouched) &&
      this.hasInvalidDescription()
    );
  }

  isDescriptionInvalid(): boolean {
    return (
      this.showDescriptionRequiredError() || this.showDescriptionCharError()
    );
  }

  showOccurredAtRequiredError(): boolean {
    return (
      (this.formValidateAttempted || this.occurredAtTouched) &&
      !this.draft.occurred_at
    );
  }

  /** True when value is present but not a valid wall-clock datetime-local. */
  showOccurredAtFormatError(): boolean {
    if (
      !(this.formValidateAttempted || this.occurredAtTouched) ||
      !this.draft.occurred_at
    ) {
      return false;
    }
    return this.parseOccurredAtParts(this.draft.occurred_at) == null;
  }

  showOccurredAtFutureError(): boolean {
    if (
      !(this.formValidateAttempted || this.occurredAtTouched) ||
      !this.draft.occurred_at ||
      this.showOccurredAtFormatError()
    ) {
      return false;
    }
    const parts = this.parseOccurredAtParts(this.draft.occurred_at);
    if (!parts) {
      return false;
    }
    const selected = new Date(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      0,
      0
    );
    const now = new Date();
    now.setSeconds(0, 0);
    return selected.getTime() > now.getTime();
  }

  showOccurredAtMinError(): boolean {
    if (
      !(this.formValidateAttempted || this.occurredAtTouched) ||
      !this.draft.occurred_at ||
      this.showOccurredAtFormatError()
    ) {
      return false;
    }
    const parts = this.parseOccurredAtParts(this.draft.occurred_at);
    if (!parts) {
      return false;
    }
    return (
      parts.year < 2018 ||
      moment(this.draft.occurred_at).isBefore(moment(this.minOccurredAt))
    );
  }

  isOccurredAtInvalid(): boolean {
    return (
      this.showOccurredAtRequiredError() ||
      this.showOccurredAtFormatError() ||
      this.showOccurredAtFutureError() ||
      this.showOccurredAtMinError()
    );
  }

  /**
   * Allow picker / navigation keys, but block free typing of absurd years
   * (date/datetime-local + readonly often disables the native picker).
   */
  blockDateTyping(event: KeyboardEvent): void {
    const allowed = [
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Backspace',
      'Delete'
    ];
    if (allowed.includes(event.key) || event.ctrlKey || event.metaKey) {
      return;
    }
    event.preventDefault();
  }

  /**
   * Strict YYYY-MM-DDTHH:mm with a realistic 4-digit year (rejects 200000, etc.).
   */
  private parseOccurredAtParts(
    value: string
  ): {
    year: number;
    month: number;
    day: number;
    hour: number;
    minute: number;
  } | null {
    const m = String(value || '')
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!m) {
      return null;
    }
    const year = parseInt(m[1], 10);
    const month = parseInt(m[2], 10);
    const day = parseInt(m[3], 10);
    const hour = parseInt(m[4], 10);
    const minute = parseInt(m[5], 10);
    if (
      year < 2018 ||
      year > 2100 ||
      month < 1 ||
      month > 12 ||
      day < 1 ||
      day > 31 ||
      hour > 23 ||
      minute > 59
    ) {
      return null;
    }
    const dt = new Date(year, month - 1, day, hour, minute, 0, 0);
    if (
      dt.getFullYear() !== year ||
      dt.getMonth() !== month - 1 ||
      dt.getDate() !== day ||
      dt.getHours() !== hour ||
      dt.getMinutes() !== minute
    ) {
      return null;
    }
    return { year, month, day, hour, minute };
  }

  showConflictStatusRequiredError(): boolean {
    return (
      (this.formValidateAttempted || this.conflictStatusTouched) &&
      !this.draft.conflict_status
    );
  }

  isConflictStatusInvalid(): boolean {
    return this.showConflictStatusRequiredError();
  }

  showPriorityRequiredError(): boolean {
    return (
      (this.formValidateAttempted || this.priorityTouched) &&
      !this.draft.priority
    );
  }

  isPriorityInvalid(): boolean {
    return this.showPriorityRequiredError();
  }

  /**
   * Save is shown only when required fields are filled and no validation
   * errors remain (independent of touched / submit-attempt state).
   */
  get canSaveForm(): boolean {
    if (this.saving || this.hasUploadingEvidence()) {
      return false;
    }

    const description = (this.draft.description || '').trim();
    if (!description || hasInvalidIncidentLongText(description)) {
      return false;
    }

    if (!this.draft.occurred_at) {
      return false;
    }
    const parts = this.parseOccurredAtParts(this.draft.occurred_at);
    if (!parts) {
      return false;
    }
    if (parts.year < 2018) {
      return false;
    }
    const selected = new Date(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      0,
      0
    );
    const now = new Date();
    now.setSeconds(0, 0);
    if (selected.getTime() > now.getTime()) {
      return false;
    }
    if (moment(this.draft.occurred_at).isBefore(moment(this.minOccurredAt))) {
      return false;
    }

    if (!this.draft.conflict_status || !this.draft.priority) {
      return false;
    }

    if (this.hasInvalidEvidenceUrls()) {
      return false;
    }
    if (this.hasFutureReviewedAt()) {
      return false;
    }
    if (this.hasEvidenceFileErrors()) {
      return false;
    }
    if (this.hasIncompleteEvidences()) {
      return false;
    }

    if (this.draft.published && !this.canPublishUpdate) {
      return false;
    }

    const isUnpublishing =
      this.editingId != null && this.wasPublishedOnEdit && !this.draft.published;
    if (isUnpublishing) {
      const reason = (this.draft.unpublish_reason || '').trim();
      if (!reason || hasInvalidIncidentLongText(reason)) {
        return false;
      }
    }

    if ((this.draft.evidences || []).length > this.maxEvidences) {
      return false;
    }

    return true;
  }

  hasEvidenceFileErrors(): boolean {
    return (this.draft.evidences || []).some(e => !!e.fileErrorKey);
  }

  /** Each evidence row must have a file and/or a link. */
  hasIncompleteEvidences(): boolean {
    return (this.draft.evidences || []).some(evidence => {
      const hasFile = !!(evidence.media_id || evidence.pendingFile);
      const hasLink = !!(evidence.source_url || '').trim();
      return !hasFile && !hasLink;
    });
  }

  /** Empty link is ok; non-empty must pass evidence URL rules. */
  isValidEvidenceUrl(value: string | null | undefined): boolean {
    return isValidEvidenceUrl(value);
  }

  getEvidenceUrlErrorKey(evidence: { source_url?: string }): string | null {
    if (!(this.formValidateAttempted || this.evidenceUrlTouched)) {
      return null;
    }
    return getEvidenceUrlError(evidence?.source_url);
  }

  isEvidenceUrlInvalid(evidence: { source_url?: string }): boolean {
    return !!this.getEvidenceUrlErrorKey(evidence);
  }

  hasInvalidEvidenceUrls(): boolean {
    return (this.draft.evidences || []).some(
      e => getEvidenceUrlError(e?.source_url) != null
    );
  }

  onEvidenceUrlBlur(evidence: EvidenceDraft): void {
    this.evidenceUrlTouched = true;
    if (evidence && evidence.source_url != null) {
      evidence.source_url = normalizeEvidenceUrl(
        String(evidence.source_url).replace(/\s+/g, '')
      );
    }
  }

  onEvidenceUrlChange(evidence: EvidenceDraft): void {
    this.evidenceUrlTouched = true;
    if (evidence && evidence.source_url != null && /\s/.test(evidence.source_url)) {
      evidence.source_url = String(evidence.source_url).replace(/\s+/g, '');
    }
  }

  onEvidenceUrlKeydown(event: KeyboardEvent): void {
    if (event.key === ' ' || event.code === 'Space') {
      event.preventDefault();
    }
  }

  onEvidenceUrlPaste(event: ClipboardEvent, evidence: EvidenceDraft): void {
    event.preventDefault();
    const pasted = event.clipboardData?.getData('text') || '';
    const cleaned = pasted.replace(/\s+/g, '');
    const input = event.target as HTMLInputElement;
    const start = input?.selectionStart ?? (evidence.source_url || '').length;
    const end = input?.selectionEnd ?? start;
    const current = evidence.source_url || '';
    evidence.source_url = (
      current.slice(0, start) +
      cleaned +
      current.slice(end)
    ).slice(0, 2048);
    this.evidenceUrlTouched = true;
  }

  private validateFormFields(): boolean {
    this.formValidateAttempted = true;
    this.descriptionTouched = true;
    this.occurredAtTouched = true;
    this.conflictStatusTouched = true;
    this.priorityTouched = true;
    this.evidenceUrlTouched = true;

    (this.draft.evidences || []).forEach(e => {
      if (e?.source_url != null) {
        e.source_url = normalizeEvidenceUrl(e.source_url);
      }
    });

    if (this.isDescriptionInvalid()) {
      return false;
    }
    if (this.isOccurredAtInvalid()) {
      return false;
    }
    if (this.hasFutureReviewedAt()) {
      return false;
    }
    if (this.hasInvalidEvidenceUrls()) {
      return false;
    }
    if (this.hasEvidenceFileErrors()) {
      return false;
    }
    if (this.hasIncompleteEvidences()) {
      return false;
    }
    if (this.isConflictStatusInvalid()) {
      return false;
    }
    if (this.isPriorityInvalid()) {
      return false;
    }
    if (this.draft.published && !this.canPublishUpdate) {
      return false;
    }
    return true;
  }

  onOccurredAtBlur(): void {
    this.occurredAtTouched = true;
  }

  onOccurredAtChange(): void {
    this.occurredAtTouched = true;
  }

  onConflictStatusBlur(): void {
    this.conflictStatusTouched = true;
  }

  onPriorityBlur(): void {
    this.priorityTouched = true;
  }

  onConflictStatusChange(): void {
    this.conflictStatusTouched = true;
  }

  onPriorityChange(): void {
    this.priorityTouched = true;
  }

  private closeFormModal(reason: string = 'saved') {
    if (this.formModalRef) {
      this.formModalRef.close(reason);
      return;
    }
    this.resetFormState();
  }

  get canAddEvidence(): boolean {
    return (this.draft.evidences || []).length < this.maxEvidences;
  }

  onAddEvidence() {
    if (!this.canAddEvidence) {
      this.errorMessage = 'POST_UPDATE_EVIDENCE_MAX';
      return;
    }
    this.errorMessage = '';
    this.draft.evidences = [
      ...this.draft.evidences,
      { source_url: '', media_id: null, caption: '', reviewed_at: '' }
    ];
  }

  onRemoveEvidence(index: number) {
    if (this.draft.evidences[index]) {
      this.revokeEvidenceLocalUrl(this.draft.evidences[index]);
    }
    this.draft.evidences = this.draft.evidences.filter((_, i) => i !== index);
  }

  onClearEvidenceFile(index: number) {
    if (!this.draft.evidences[index]) {
      return;
    }
    this.revokeEvidenceLocalUrl(this.draft.evidences[index]);
    this.draft.evidences[index].media_id = null;
    this.draft.evidences[index].pendingFile = null;
    this.draft.evidences[index].caption = '';
    this.draft.evidences[index].file_url = '';
    this.draft.evidences[index].uploading = false;
    this.draft.evidences[index].fileErrorKey = '';
  }

  /** True when a file is attached locally or already stored as media. */
  hasEvidenceFile(evidence: EvidenceDraft): boolean {
    return !!(evidence?.media_id || evidence?.pendingFile);
  }

  getEvidenceFileKind(evidence: PostUpdateEvidence | EvidenceDraft): string {
    if (evidence?.media_id || (evidence as EvidenceDraft).pendingFile) {
      return this.getEvidenceMediaKind(evidence);
    }
    if (evidence?.source_url) {
      return 'url';
    }
    return 'file';
  }

  /** File type badge from media/pending file only (ignores source_url). */
  getEvidenceMediaKind(evidence: PostUpdateEvidence | EvidenceDraft): string {
    const pending = (evidence as EvidenceDraft).pendingFile;
    if (pending) {
      const name = (pending.name || '').toLowerCase();
      const mime = (pending.type || '').toLowerCase();
      if (name.endsWith('.pdf') || mime === 'application/pdf') {
        return 'pdf';
      }
      if (
        name.endsWith('.png') ||
        name.endsWith('.jpg') ||
        name.endsWith('.jpeg') ||
        mime.startsWith('image/')
      ) {
        return 'img';
      }
      return 'file';
    }

    const mediaId = evidence.media_id ? Number(evidence.media_id) : null;
    const preview = mediaId ? this.mediaPreviews[mediaId] : null;
    const label = (
      evidence.caption ||
      preview?.caption ||
      preview?.url ||
      (evidence as PostUpdateEvidence).file_url ||
      ''
    ).toLowerCase();
    const mime = (
      (evidence as PostUpdateEvidence).mime ||
      preview?.mime ||
      ''
    ).toLowerCase();
    const source = `${label} ${mime}`;
    if (source.includes('.pdf') || source.includes('application/pdf')) {
      return 'pdf';
    }
    if (
      source.includes('.png') ||
      source.includes('.jpg') ||
      source.includes('.jpeg') ||
      source.includes('image/')
    ) {
      return 'img';
    }
    return 'file';
  }

  async onEvidenceFileSelected(event: Event, index: number) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file || !this.draft.evidences[index]) {
      return;
    }
    this.draft.evidences[index].fileErrorKey = '';
    const result = await assertReadableUploadFile(file, {
      kind: 'pdf-or-image',
      maxMb: 10
    });
    if (result.ok === false) {
      this.draft.evidences[index].fileErrorKey =
        result.errorKey === 'FILE_SIZE_EXCEEDED'
          ? 'POST_UPDATE_EVIDENCE_SIZE_ERROR'
          : result.errorKey === 'FILE_CORRUPT_OR_UNREADABLE'
          ? 'FILE_CORRUPT_OR_UNREADABLE'
          : 'FILE_TYPE_PDF_OR_IMAGE';
      if (input) {
        input.value = '';
      }
      return;
    }
    // Keep the file in the draft only; upload happens on save.
    this.revokeEvidenceLocalUrl(this.draft.evidences[index]);
    this.draft.evidences[index].pendingFile = file;
    this.draft.evidences[index].media_id = null;
    this.draft.evidences[index].caption = file.name || '';
    this.draft.evidences[index].file_url = URL.createObjectURL(file);
    this.draft.evidences[index].uploading = false;
    this.draft.evidences[index].fileErrorKey = '';
    if (input) {
      input.value = '';
    }
  }

  private isAllowedEvidenceFile(file: File): boolean {
    const name = (file.name || '').toLowerCase();
    const ext = name.includes('.') ? name.split('.').pop() : '';
    const mime = (file.type || '').toLowerCase();
    const extOk =
      !!ext && EVIDENCE_ALLOWED_EXTENSIONS.indexOf(ext) !== -1;
    const mimeOk =
      !mime || EVIDENCE_ALLOWED_MIME_TYPES.indexOf(mime) !== -1;
    return extOk && mimeOk;
  }

  hasUploadingEvidence(): boolean {
    return (this.draft.evidences || []).some(e => !!e.uploading);
  }

  hasFutureReviewedAt(): boolean {
    const today = moment().startOf('day');
    return (this.draft.evidences || []).some(evidence => {
      const reviewedAt = (evidence.reviewed_at || '').trim();
      if (!reviewedAt) {
        return false;
      }
      const date = moment(reviewedAt, 'YYYY-MM-DD', true);
      return date.isValid() && date.isAfter(today);
    });
  }

  onPublishToggle(event: Event) {
    const input = event.target as HTMLInputElement;
    const wantPublished = !!input?.checked;
    if (wantPublished) {
      this.draft.published = true;
      this.draft.unpublish_reason = '';
      return;
    }

    // Never persisted as published: allow turning off without sustento.
    if (!this.wasPublishedOnEdit) {
      this.draft.published = false;
      this.draft.unpublish_reason = '';
      return;
    }

    // Keep published until sustento is confirmed.
    input.checked = true;
    this.draft.published = true;
    this.unpublishReason = '';
    this.unpublishReasonErrorKey = null;
    this.modalService.open(this.unpublishModal, {
      ariaLabelledBy: 'unpublish-update-modal-title',
      centered: true,
      backdrop: 'static'
    });
  }

  onUnpublishReasonKeypress(event: KeyboardEvent): void {
    blockIncidentLongTextKeypress(event);
  }

  onUnpublishReasonPaste(event: ClipboardEvent): void {
    blockIncidentLongTextPaste(event);
  }

  onUnpublishReasonChange(): void {
    this.unpublishReasonErrorKey = null;
  }

  confirmUpdateUnpublish(modal) {
    const reason = (this.unpublishReason || '').trim();
    if (!reason) {
      this.unpublishReasonErrorKey = 'UNPUBLISH_REASON_REQUIRED';
      return;
    }
    if (hasInvalidIncidentLongText(reason)) {
      this.unpublishReasonErrorKey = 'FORM_NO_SPECIAL_CHARACTER';
      return;
    }
    this.unpublishReasonErrorKey = null;
    this.draft.published = false;
    this.draft.unpublish_reason = reason;
    modal.close('confirm');
  }

  hasInvalidDescription(): boolean {
    return hasInvalidIncidentLongText(this.draft?.description);
  }

  onDescriptionKeypress(event: KeyboardEvent): void {
    blockIncidentLongTextKeypress(event);
  }

  onDescriptionPaste(event: ClipboardEvent): void {
    blockIncidentLongTextPaste(event);
  }

  onDescriptionBlur(): void {
    this.descriptionTouched = true;
  }

  onSave() {
    this.errorMessage = '';
    if (this.hasUploadingEvidence()) {
      this.errorMessage = 'POST_UPDATE_EVIDENCE_WAIT_UPLOAD';
      return;
    }
    if ((this.draft.evidences || []).length > this.maxEvidences) {
      this.errorMessage = 'POST_UPDATE_EVIDENCE_MAX';
      return;
    }
    if (!this.validateFormFields()) {
      if (this.hasFutureReviewedAt()) {
        this.errorMessage = 'POST_UPDATE_EVIDENCE_REVIEWED_AT_FUTURE';
      } else if (this.draft.published && !this.canPublishUpdate) {
        this.errorMessage = 'POST_UPDATE_PUBLISH_REQUIRES_ROOT';
      }
      return;
    }

    const description = (this.draft.description || '').trim();

    const wantPublished =
      this.editingId != null
        ? !!this.draft.published
        : !!this.draft.published && this.canPublishUpdate;
    const publishChanged =
      this.editingId == null || wantPublished !== this.wasPublishedOnEdit;
    const isUnpublishing =
      this.editingId != null && this.wasPublishedOnEdit && !wantPublished;
    if (isUnpublishing && !(this.draft.unpublish_reason || '').trim()) {
      this.errorMessage = 'UNPUBLISH_REASON_REQUIRED';
      return;
    }
    if (
      isUnpublishing &&
      hasInvalidIncidentLongText((this.draft.unpublish_reason || '').trim())
    ) {
      this.errorMessage = 'FORM_NO_SPECIAL_CHARACTER';
      return;
    }

    this.saving = true;
    this.subs.add(
      this.uploadPendingEvidenceFiles().subscribe({
        next: () => {
          const payload: PostUpdatePayload = {
            description,
            occurred_at: this.toMysqlDateTime(this.draft.occurred_at),
            conflict_status: this.draft.conflict_status,
            priority: this.draft.priority,
            evidences: this.normalizeEvidences(this.draft.evidences)
          };

          // Never touch published unless it really changed (avoids false unpublish).
          if (publishChanged) {
            payload.published = wantPublished;
            if (isUnpublishing) {
              payload.unpublish_reason = (
                this.draft.unpublish_reason || ''
              ).trim();
            }
          }

          const request$ =
            this.editingId != null
              ? this.postService.updateUpdate(
                  this.rootPostId,
                  this.editingId,
                  payload
                )
              : this.postService.createUpdate(this.rootPostId, payload);

          this.subs.add(
            request$.subscribe({
              next: () => {
                this.saving = false;
                this.closeFormModal('saved');
                this.load();
              },
              error: err => {
                this.saving = false;
                this.handleSaveError(err);
              }
            })
          );
        },
        error: () => {
          this.saving = false;
          this.errorMessage = 'POST_UPDATE_EVIDENCE_UPLOAD_ERROR';
        }
      })
    );
  }

  /** Upload draft-only files; nothing hits media DB until save. */
  private uploadPendingEvidenceFiles() {
    const pending = (this.draft.evidences || []).filter(e => !!e.pendingFile);
    if (!pending.length) {
      return of(null);
    }

    return forkJoin(
      pending.map(evidence => {
        const file = evidence.pendingFile as File;
        evidence.uploading = true;
        evidence.fileErrorKey = '';
        return this.mediaService.attach(file).pipe(
          tap(media => {
            const mediaId = Number(media.id);
            const url = media.original_file_url || '';
            this.revokeEvidenceLocalUrl(evidence);
            evidence.media_id = mediaId;
            evidence.caption = media.caption || file.name || evidence.caption;
            evidence.file_url = url;
            evidence.pendingFile = null;
            evidence.uploading = false;
            if (mediaId && url) {
              this.mediaPreviews[mediaId] = {
                url,
                caption: media.caption || file.name || '',
                mime: media.mime
              };
            }
          }),
          catchError(err => {
            evidence.uploading = false;
            evidence.fileErrorKey = 'POST_UPDATE_EVIDENCE_UPLOAD_ERROR';
            return throwError(err);
          })
        );
      })
    ).pipe(map(() => null));
  }

  private handleSaveError(err: any) {
    const publishedError =
      err?.error?.errors?.published || err?.error?.messages?.published;
    const reasonError =
      err?.error?.errors?.unpublish_reason ||
      err?.error?.messages?.unpublish_reason;
    const evidencesError =
      err?.error?.errors?.evidences || err?.error?.messages?.evidences;
    const occurredAtError =
      err?.error?.errors?.occurred_at || err?.error?.messages?.occurred_at;
    if (publishedError) {
      this.errorMessage = 'POST_UPDATE_PUBLISH_REQUIRES_ROOT';
    } else if (reasonError) {
      this.errorMessage = 'UNPUBLISH_REASON_REQUIRED';
    } else if (evidencesError) {
      const evidenceMsg = JSON.stringify(evidencesError).toLowerCase();
      this.errorMessage = evidenceMsg.includes('future')
        ? 'POST_UPDATE_EVIDENCE_REVIEWED_AT_FUTURE'
        : evidenceMsg.includes('url')
        ? 'POST_UPDATE_EVIDENCE_LINK_INVALID'
        : 'POST_UPDATE_EVIDENCE_MAX';
    } else if (occurredAtError) {
      const occurredMsg = JSON.stringify(occurredAtError).toLowerCase();
      this.errorMessage = occurredMsg.includes('future')
        ? 'POST_UPDATE_OCCURRED_AT_FUTURE'
        : 'FORM_INVALID_DATE_FORMAT';
    } else {
      this.errorMessage = 'POST_UPDATE_SAVE_ERROR';
    }
  }

  onDelete(update: PostUpdate) {
    if (!update?.id || !this.canManage) {
      return;
    }
    if (update.published) {
      this.errorMessage = 'POST_UPDATE_DELETE_PUBLISHED';
      return;
    }
    this.subs.add(
      this.postService.deleteUpdate(this.rootPostId, update.id).subscribe({
        next: () => this.load(),
        error: err => {
          const forbidden =
            err?.status === 403 ||
            err?.error?.errors?.[0]?.status === 403;
          this.errorMessage = forbidden
            ? 'POST_UPDATE_DELETE_PUBLISHED'
            : 'POST_UPDATE_DELETE_ERROR';
        }
      })
    );
  }

  formatOccurredAt(value: string | Date): string {
    if (!value) {
      return '';
    }
    const match = String(value).match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/
    );
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]} ${match[4]}:${match[5]}`;
    }
    return moment(value).format('DD/MM/YYYY HH:mm');
  }

  formatReviewedAt(value: string | Date): string {
    return value ? moment(value).format('DD/MM/YYYY') : '';
  }

  getEvidenceLabel(evidence: PostUpdateEvidence | EvidenceDraft): string {
    return (
      this.getEvidenceFileLabel(evidence) ||
      this.getEvidenceLinkUrl(evidence) ||
      ''
    );
  }

  getEvidenceLinkUrl(evidence: PostUpdateEvidence | EvidenceDraft): string {
    return (evidence?.source_url || '').trim();
  }

  getEvidenceFileLabel(evidence: PostUpdateEvidence | EvidenceDraft): string {
    if (evidence.caption) {
      return evidence.caption;
    }
    const mediaId = evidence.media_id ? Number(evidence.media_id) : null;
    if (mediaId && this.mediaPreviews[mediaId]?.caption) {
      return this.mediaPreviews[mediaId].caption;
    }
    if (mediaId) {
      return `archivo-${mediaId}`;
    }
    const pending = (evidence as EvidenceDraft).pendingFile;
    if (pending?.name) {
      return pending.name;
    }
    return '';
  }

  getEvidenceFileUrl(evidence: PostUpdateEvidence | EvidenceDraft): string {
    const directUrl =
      (evidence as EvidenceDraft).file_url ||
      (evidence as PostUpdateEvidence).file_url;
    if (directUrl) {
      return directUrl;
    }
    const mediaId = evidence.media_id ? Number(evidence.media_id) : null;
    return mediaId && this.mediaPreviews[mediaId]
      ? this.mediaPreviews[mediaId].url
      : '';
  }

  /** Prefer file URL when both link and file exist (used by form attach UI). */
  getEvidenceUrl(evidence: PostUpdateEvidence | EvidenceDraft): string {
    return (
      this.getEvidenceFileUrl(evidence) || this.getEvidenceLinkUrl(evidence)
    );
  }

  openEvidence(evidence: PostUpdateEvidence | EvidenceDraft): void {
    const url = this.getEvidenceUrl(evidence);
    if (!url) {
      return;
    }
    window.open(url, '_blank', 'noopener');
  }

  downloadEvidence(evidence: PostUpdateEvidence | EvidenceDraft): void {
    this.downloadEvidenceFile(evidence);
  }

  downloadEvidenceFile(evidence: PostUpdateEvidence | EvidenceDraft): void {
    const url = this.getEvidenceFileUrl(evidence);
    if (!url) {
      return;
    }
    const fileName =
      this.getEvidenceFileLabel(evidence) ||
      getFileNameFromMediaUrl(url, 'evidence');
    if (url.startsWith('blob:')) {
      const a = document.createElement('a');
      a.href = url;
      a.download = String(fileName);
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300 && xhr.response) {
        const blob = xhr.response;
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = String(fileName);
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
        return;
      }
      // Anonymous / CORS: open the public file URL instead.
      window.open(url, '_blank', 'noopener');
    };
    xhr.onerror = () => {
      window.open(url, '_blank', 'noopener');
    };
    xhr.open('GET', url);
    xhr.send();
  }

  private resolveEvidenceMedia(updates: PostUpdate[]): void {
    const idsToFetch = new Set<number>();
    (updates || []).forEach(update => {
      (update.evidences || []).forEach(evidence => {
        const id = evidence?.media_id ? Number(evidence.media_id) : null;
        if (!id || this.mediaPreviews[id]) {
          return;
        }
        // Prefer file_url from the updates payload (works for anonymous viewers).
        if (evidence.file_url) {
          this.mediaPreviews[id] = {
            url: evidence.file_url,
            caption: evidence.caption || '',
            mime: evidence.mime || undefined
          };
          return;
        }
        idsToFetch.add(id);
      });
    });
    // Trigger change detection for seeded previews.
    this.mediaPreviews = { ...this.mediaPreviews };

    idsToFetch.forEach(id => {
      this.subs.add(
        this.mediaService.read(id).subscribe({
          next: media => {
            if (!media?.original_file_url) {
              return;
            }
            this.mediaPreviews = {
              ...this.mediaPreviews,
              [id]: {
                url: media.original_file_url,
                caption: media.caption || '',
                mime: media.mime
              }
            };
            // Keep edit-form draft in sync if open.
            (this.draft.evidences || []).forEach(item => {
              if (Number(item.media_id) === id) {
                item.file_url = media.original_file_url;
                if (!item.caption && media.caption) {
                  item.caption = media.caption;
                }
              }
            });
          },
          error: () => {
            // Leave unresolved; UI shows placeholder without link.
          }
        })
      );
    });
  }

  private emptyDraft() {
    return {
      description: '',
      occurred_at: '',
      conflict_status: '',
      priority: '',
      published: false,
      unpublish_reason: '',
      evidences: [] as EvidenceDraft[]
    };
  }

  private revokeEvidenceLocalUrl(evidence: EvidenceDraft) {
    if (evidence?.file_url && evidence.file_url.startsWith('blob:')) {
      URL.revokeObjectURL(evidence.file_url);
      evidence.file_url = '';
    }
  }

  private revokeAllDraftLocalUrls() {
    (this.draft.evidences || []).forEach(e => this.revokeEvidenceLocalUrl(e));
  }

  private toDatetimeLocal(value: string | Date): string {
    if (!value) {
      return '';
    }
    // Digits-only wall-clock (no TZ shift via moment local/utc).
    const match = String(value).match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/
    );
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}`;
    }
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-MM-DDTHH:mm') : '';
  }

  /** Naive wall-clock for API/DB — never toISOString() (that applies TZ). */
  private toMysqlDateTime(value: string): string {
    const normalized = String(value || '')
      .trim()
      .replace(' ', 'T');
    const withMinutes = normalized.match(
      /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/
    );
    const parts = this.parseOccurredAtParts(
      withMinutes ? withMinutes[1] : normalized
    );
    if (!parts) {
      return value;
    }
    const p = (n: number) => String(n).padStart(2, '0');
    return `${parts.year}-${p(parts.month)}-${p(parts.day)} ${p(
      parts.hour
    )}:${p(parts.minute)}:00`;
  }

  private toDateInput(value: string | Date | null | undefined): string {
    if (!value) {
      return '';
    }
    const m = moment(value);
    return m.isValid() ? m.format('YYYY-MM-DD') : '';
  }

  private normalizeEvidences(items: EvidenceDraft[]): PostUpdateEvidence[] {
    return (items || [])
      .map(item => {
        const evidence: PostUpdateEvidence = {};
        if (item.media_id) {
          evidence.media_id = item.media_id;
        }
        const url = normalizeEvidenceUrl(item.source_url || '');
        if (url && isValidEvidenceUrl(url)) {
          evidence.source_url = url;
        }
        if (item.caption) {
          evidence.caption = item.caption;
        }
        const reviewedAt = (item.reviewed_at || '').trim();
        if (reviewedAt) {
          evidence.reviewed_at = reviewedAt;
        }
        return evidence;
      })
      .filter(e => !!e.media_id || !!e.source_url);
  }
}
